import cx from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { FunctionComponent, useMemo, useState } from 'react';

import useLinkProps from '../../../hooks/use-link-props';
import { escapeRegExp } from '../../../utils/escapeRegExp';
import TextInput from '../../ui/inputs/text';
import SearchInput from '../v2/vehicle/search-input';
import CategoryHeader from '../../ui/categories/header';
import ProductCard from '../../ui/v2/products/listing';
import CollapsibleList from '../../ui/collapsible-list/menu';
import { searchNormalized, sortedPerAvailabilityList } from './utils';
import useSortedPerAvailabilityList from '../../../hooks/use-sorted-per-availability-list';

const StockCollapsible: FunctionComponent<{
  groups: TCategories;
  products: TVehicle['products'];
  vehicle: {
    brand: string;
    model: string;
    version: string | undefined;
  };
}> = ({ groups, products, vehicle }) => {
  /*
    category.match(new RegExp(escapeRegExp(search), 'i')) ||
              part.match(new RegExp(escapeRegExp(search), 'i')) ||
              sku.match(new RegExp(escapeRegExp(search), 'i')) ||
              (reference &&
                reference.match(new RegExp(escapeRegExp(search), 'i')))
                */
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const [search, setSearch] = useState<string>('');

  const groupsSort: TCategoriesWithProducts[] = useMemo(() => {
    let sortedGroups: TCategoriesWithProducts[] = [];
    groups.forEach(({ id, slug, name }, index) => {
      const categoryInfo = products.find(
        (cat) => cat.category_slug === slug
      );
      if (categoryInfo) {
        sortedGroups.push({ id, slug, name, products: categoryInfo.products, total_products: categoryInfo.total })
      } else {
        sortedGroups.push({ id, slug, name, products: [], total_products: 0 })
      }
    });

    return sortedPerAvailabilityList(sortedGroups);

  }, [groups]);

  const filteredProducts: TVehicle['products'] = useMemo(() => {
    if (search) {
      const filtered = products
        .map(({ products, ...category }) => ({
          ...category,
          products: products.filter(
            ({ category, part, sku, reference }) =>
              searchNormalized(category, search) ||
              searchNormalized(part, search) ||
              searchNormalized(sku, search) ||
              (reference && searchNormalized(reference, search))
          ),
        }))
        .filter((category) => category.products.length > 0);
      return filtered;
    }

    return products;
  }, [search, products]);

  const onOpen = ({ products }: { products: Array<TProductListItem> }) => {
    if (window?.dataLayer && products && products.length > 0) {
      // @ts-ignore
      window.dataLayer.push({
        event: 'eec.impressionView',
        ecommerce: {
          currencyCode: 'EUR',
          impressions: products.map((item, index) => ({
            list: 'Vehicle Page', // List name where the impression was viewed
            id: item.sku, // SKU of the product
            name: item.part_en, // name of the product.
            brand: item.car_brand, // car brand of the part
            category: item.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
            price: item.pricing?.price_product
              ? Number(item.pricing.price_product).toFixed(2)
              : null, // price of the part without shipping, tax and currency
            position: index, // the position of the item in the list
            dimension2: item.reference, // reference of the product
            dimension3: item.car_model, // car model of the part
            dimension4: item?.car_version, // car version of the part
          })),
        },
      });

      // GA4
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          currency: 'EUR', // Currency of the items associated with the event, in 3-letter ISO 4217 format
          items: products.map((item, index) => ({
            item_list_name: 'Vehicle Page', // List name where the impression was viewed
            item_list_id: 'vehicle-page', // List name where the impression was viewed
            item_id: item.sku, // SKU of the product
            item_name: item.part_en, // name of the product.
            item_brand: item.car_brand, // car brand of the part
            item_category: item.category_en, // Product category(ies) - each "/" is a category level (max 5 levels)
            item_category2: item.part_en, // part name === product name.
            price: item.pricing?.price_product
              ? Number(item.pricing.price_product).toFixed(2)
              : null, // price of the part without shipping, tax and currency
            index: index, // the position of the item in the list
            quantity: '1', // Hardcoded to 1
          })),
        },
      });
    }
  };

  const gtmEventOnClick = (product, indexProd) => {
    window?.dataLayer.push({
      event: 'eec.impressionClick',
      ecommerce: {
        currencyCode: 'EUR', // currency code
        click: {
          actionField: {
            list: 'Vehicle Page', // List name where the impression click occured
          },
          products: [
            {
              id: product.sku, // SKU of the product
              name: product.part_en, // name of the product.
              brand: product.car_brand, // car brand of the part
              category: product.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
              price: product.pricing?.price_product
                ? Number(product.pricing.price_product).toFixed(2)
                : null, // price of the part without shipping, tax and currency
              position: indexProd, // the position of the item in the list
              dimension2: product.reference, // reference of the product
              dimension3: product.car_model, // car model of the part
              dimension4: product?.car_version, // car version of the part
            },
          ],
        },
      },
    });

    // GA4
    window?.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window?.dataLayer.push({
      event: 'select_item',
      ecommerce: {
        currency: 'EUR',
        items: [
          {
            item_name: product.part_en,
            item_id: product.sku,
            price: product.pricing?.price_product
              ? Number(product.pricing.price_product).toFixed(2)
              : null,
            item_brand: product.car_brand,
            item_category: product.category_en,
            item_category2: product.part_en,
            item_list_name: 'Vehicle Page',
            item_list_id: 'vehicle-page',
            index: indexProd,
            quantity: '1',
          },
        ],
      },
    });
  };

  return (
    <>
      <div
        className={cx(
          'flex',
          'w-full max-lg:px-4',
          'mx-auto',
          'mb-4',
          'my-5'
        )}
      >
        <SearchInput
          search={search}
          placeholder={t('common:filterByPart')}
          setSearch={setSearch}
          isFullWith={true}
        />
      </div>

      {search ? (
        <div>
          {filteredProducts.map((categoryInfo, index) => (
            <div key={categoryInfo.category_slug}>
              <div className={cx('py-4', 'lg:px-4')}>
                <CategoryHeader
                  index={index}
                  stock={categoryInfo?.products.length || 0}
                  id={
                    groups.find((el) => el.slug === categoryInfo.category_slug)
                      ?.id
                  }
                  name={categoryInfo.category}
                  hideImage={true}
                />
              </div>
              <div
                className={cx(
                  'grid',
                  'lg:grid-cols-4',
                  'py-2',
                  'gap-4',
                  'lg:px-4'
                )}
              >
                {categoryInfo?.products.map((product, indexProd) => (
                  <Link
                    key={product.slug}
                    {...getLink('product', {
                      category: product.category_slug,
                      subCategory: product.slug,
                    })}
                    passHref
                    legacyBehavior
                  >
                    <ProductCard
                      as="a"
                      sku={product.sku}
                      car={vehicle}
                      part={product.part}
                      reference={product.reference}
                      pricing={product.pricing}
                      main_image={product.main_image}
                      warranty={product.warranty}
                      showSku
                      onClick={() => gtmEventOnClick(product, indexProd)}
                    />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="max-lg:px-4">
          <CollapsibleList
            items={groupsSort.map(({ id, slug, name, total_products, products }, index) => {

              return {
                key: slug,
                disabled: total_products === 0,
                onOpen: () => onOpen({ products }),
                header: (
                  <CategoryHeader
                    stock={products.length || 0}
                    id={id}
                    name={name}
                    index={index}
                    hideImage={true}
                  />
                ),
                content: (
                  <div className={cx('py-4 mx-[1px]')}>
                    <ul className={cx('grid', 'gap-4', 'lg:grid-cols-4')}>
                      {products.map((product, indexProd) => (
                        <li key={product.slug} className='w-full'>
                          <Link
                            {...getLink('product', {
                              category: product.category_slug,
                              subCategory: product.slug,
                            })}
                            passHref
                            legacyBehavior
                          >
                            <ProductCard
                              as="a"
                              sku={product.sku}
                              car={vehicle}
                              part={product.part}
                              reference={product.reference}
                              pricing={product.pricing}
                              main_image={product.main_image}
                              warranty={product.warranty}
                              showSku
                              onClick={() =>
                                gtmEventOnClick(product, indexProd)
                              }
                            />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ),
              };
            })}
          />
        </div>
      )}
    </>
  );
};

export default StockCollapsible;
